// Library, collections, Home and stats (DEVELOPMENT.md: Screens). Request and response types
// shared by the api and the web app, plus the filter vocabulary both sides parse.
import type { PuzzleDoc, PuzzleSummary } from './puzzle';

/** A library row: the summary plus what the row draws. */
export interface LibraryItem extends PuzzleSummary {
  /** The document without its solution, for the thumbnail. */
  preview: PuzzleDoc;
  /** In your favourites. */
  favourite: boolean;
  /** In the other person's favourites. */
  otherFavourite: boolean;
  /** Added in the last week and not started by you. */
  isNew: boolean;
}

export interface LibraryPage {
  puzzles: LibraryItem[];
  /** Matching puzzles in all. */
  total: number;
  /** Where the next page starts, or null at the end. */
  nextOffset: number | null;
}

// ── Filters ─────────────────────────────────────────────────────────────────

export const SCORE_BANDS = ['0-2', '2-4', '4-6', '6-8', '8-10', 'unrated'] as const;
export type ScoreBand = (typeof SCORE_BANDS)[number];

/** Your status on a puzzle, or the other person's ("other-solved"). */
export const STATUS_FILTERS = [
  'new',
  'not-started',
  'in-progress',
  'solved',
  'other-solved',
] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Grid kinds in the Type filter, beside the constraint types. */
export const GRID_KINDS = ['classic', 'irregular', 'samurai', 'fog'] as const;
export type GridKind = (typeof GRID_KINDS)[number];

export const KIND_NAMES: Record<GridKind, string> = {
  classic: 'Classic',
  irregular: 'Irregular',
  samurai: 'Samurai',
  fog: 'Fog',
};

/**
 * Regions that change the boxes: listed, and not just the standard boxes (SudokuPad files list
 * the standard boxes as regions too).
 */
export function irregularRegions(grid: PuzzleDoc['grid']): boolean {
  const regions = grid.regions ?? [];
  if (regions.length === 0) return false;
  const { boxRows: br, boxCols: bc } = grid;
  if (!br || !bc) return true;
  return !regions.every((reg) => {
    if (reg.length !== br * bc) return false;
    const box = (rc: readonly number[]) => `${Math.floor(rc[0]! / br)},${Math.floor(rc[1]! / bc)}`;
    return reg.every((rc) => box(rc) === box(reg[0]!));
  });
}

/** The grid kinds a document has; "classic" when it has nothing else (no constraints either). */
export function gridKinds(doc: PuzzleDoc, constraintTypes: readonly string[]): GridKind[] {
  const kinds: GridKind[] = [];
  if (irregularRegions(doc.grid)) kinds.push('irregular');
  if ((doc.grid.subGrids?.length ?? 0) > 0) kinds.push('samurai');
  if (doc.fog) kinds.push('fog');
  if (kinds.length === 0 && constraintTypes.length === 0) kinds.push('classic');
  return kinds;
}

/** The two dailies: a classic 9×9 (no variant rules), and a variant. */
export const DAILY_KINDS = ['classic', 'variant'] as const;
export type DailyKind = (typeof DAILY_KINDS)[number];

/** Which daily a puzzle can be: classic when it's a plain 9×9 sudoku, else variant. */
export function dailyKindOf(p: {
  rows: number;
  cols: number;
  constraintTypes: readonly string[];
  document: unknown;
}): DailyKind {
  const doc = p.document as Partial<PuzzleDoc> | null;
  const plain = doc?.grid
    ? gridKinds(doc as PuzzleDoc, p.constraintTypes).includes('classic')
    : p.constraintTypes.length === 0 && !(doc as { fog?: unknown } | null)?.fog;
  return plain && p.rows === 9 && p.cols === 9 ? 'classic' : 'variant';
}

export const SORTS = ['newest', 'oldest', 'easiest', 'hardest', 'title', 'played'] as const;
export type LibrarySort = (typeof SORTS)[number];

export const SORT_NAMES: Record<LibrarySort, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  easiest: 'Easiest',
  hardest: 'Hardest',
  title: 'Title',
  played: 'Recently played',
};

export interface LibraryFilters {
  /** Words to find in titles, setters and rules. */
  q?: string;
  /** Any of these score bands. */
  score?: ScoreBand[];
  status?: StatusFilter;
  /** Constraint types (plugin ids) and grid kinds; a puzzle must have all of them. */
  type?: string[];
  /** "9x9", "6x6", ... */
  size?: string;
  /** Any of these source keys ("import", "ctc", ...). */
  source?: string[];
  origin?: 'hand-set' | 'generated';
  /** Your favourites or the other person's. */
  fav?: 'me' | 'other';
  /** Only puzzles that need a plugin. */
  plugin?: boolean;
  collection?: string;
  sort?: LibrarySort;
}

const list = (v: string | null | undefined): string[] =>
  v
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

function oneOf<T extends string>(
  values: readonly T[],
  v: string | null | undefined,
): T | undefined {
  return values.includes(v as T) ? (v as T) : undefined;
}

/** URLSearchParams, or anything else that reads one parameter. */
export interface ParamsLike {
  get(name: string): string | null;
}

/** Filters from URL search parameters (the library keeps them in its URL). Unknown values drop. */
export function parseFilters(params: ParamsLike): LibraryFilters {
  const f: LibraryFilters = {};
  const q = params.get('q')?.trim();
  if (q) f.q = q.slice(0, 100);
  const score = list(params.get('score')).filter((b): b is ScoreBand =>
    (SCORE_BANDS as readonly string[]).includes(b),
  );
  if (score.length) f.score = score;
  const status = oneOf(STATUS_FILTERS, params.get('status'));
  if (status) f.status = status;
  const type = list(params.get('type')).filter((t) => /^[a-z0-9-]{1,40}$/.test(t));
  if (type.length) f.type = type;
  const size = params.get('size');
  if (size && /^\d{1,2}x\d{1,2}$/.test(size)) f.size = size;
  const source = list(params.get('source')).filter((s) => /^[a-z0-9-]{1,40}$/.test(s));
  if (source.length) f.source = source;
  const origin = oneOf(['hand-set', 'generated'] as const, params.get('origin'));
  if (origin) f.origin = origin;
  const fav = oneOf(['me', 'other'] as const, params.get('fav'));
  if (fav) f.fav = fav;
  if (params.get('plugin') === '1') f.plugin = true;
  const collection = params.get('collection');
  if (collection && /^[a-z0-9]{1,40}$/i.test(collection)) f.collection = collection;
  const sort = oneOf(SORTS, params.get('sort'));
  if (sort && sort !== 'newest') f.sort = sort;
  return f;
}

/** The URL search parameters for filters, as pairs (the inverse of parseFilters). */
export function filterParams(f: LibraryFilters): [string, string][] {
  const p: [string, string][] = [];
  if (f.q) p.push(['q', f.q]);
  if (f.score?.length) p.push(['score', f.score.join(',')]);
  if (f.status) p.push(['status', f.status]);
  if (f.type?.length) p.push(['type', f.type.join(',')]);
  if (f.size) p.push(['size', f.size]);
  if (f.source?.length) p.push(['source', f.source.join(',')]);
  if (f.origin) p.push(['origin', f.origin]);
  if (f.fav) p.push(['fav', f.fav]);
  if (f.plugin) p.push(['plugin', '1']);
  if (f.collection) p.push(['collection', f.collection]);
  if (f.sort && f.sort !== 'newest') p.push(['sort', f.sort]);
  return p;
}

/** What the filter menus offer: only values the library has, with counts. */
export interface LibraryFacets {
  total: number;
  types: { type: string; count: number }[];
  kinds: { kind: GridKind; count: number }[];
  sizes: { size: string; count: number }[];
  sources: { key: string; name: string; count: number }[];
  origins: { origin: 'hand-set' | 'generated'; count: number }[];
  needsPlugin: number;
}

// ── Favourites and collections ─────────────────────────────────────────────

export interface FavouriteRequest {
  favourite: boolean;
}

export interface FavouriteResponse {
  favourite: boolean;
}

/** Colours a collection can have: from the Library mockup and the Appendix score scale. */
export const COLLECTION_COLOURS = [
  '#2F5FD0',
  '#E39A4B',
  '#4FB868',
  '#E0645A',
  '#F2CE5B',
  '#8E8A82',
] as const;

export interface CollectionDto {
  id: string;
  name: string;
  colour: string;
  count: number;
  createdAt: string;
}

export interface CollectionsResponse {
  collections: CollectionDto[];
}

export interface CollectionRequest {
  name: string;
  colour: string;
}

/** Put a puzzle in a collection or take it out. */
export interface CollectionMembershipRequest {
  puzzleId: string;
  member: boolean;
}

// ── Puzzle details ─────────────────────────────────────────────────────────

export interface PuzzleLink {
  source: string;
  url: string;
  title: string;
}

/** What the details screen adds to the solve screen's puzzle. */
export interface PuzzleAbout {
  favourite: boolean;
  otherFavourite: boolean;
  /** Ids of the collections the puzzle is in. */
  collections: string[];
  /** Every place the puzzle was found. */
  links: PuzzleLink[];
  /** Admin only, for needs-a-plugin puzzles: the "Copy request for Claude Code" text. */
  pluginRequest: string | null;
}

// ── Home ────────────────────────────────────────────────────────────────────

export interface DailyItem {
  /** Belgrade date, YYYY-MM-DD. */
  date: string;
  puzzle: PuzzleSummary;
  preview: PuzzleDoc;
}

/** What picking one daily did (the 06:00 job, or Admin → Generator's "Pick again"). */
export type DailyPickResult =
  | { picked: { puzzleId: string; title: string; score: number | null; handSet: boolean } }
  | { skipped: 'exists' | 'backlog' | 'no-puzzle'; backlog?: number[] };

/** Admin → Generator → "Pick again": today's daily of one kind, replaced (the job's result). */
export interface DailyRepickResult {
  date: string;
  kind: DailyKind;
  /** Today's daily before, if there was one; it stays when nothing new could be picked. */
  replaced: { title: string; score: number | null } | null;
  result: DailyPickResult;
}

/** Admin → Library tools → "Make today's daily": this puzzle in place of today's daily of its
 * kind. */
export interface DailyPromoteResult {
  date: string;
  kind: DailyKind;
  /** Today's daily before, if there was one; it stays in the library. */
  replaced: { title: string; score: number | null } | null;
}

export interface HomeResponse {
  /** The latest daily of each kind (classic 9×9 and variant), or null before the first one. */
  dailies: Record<DailyKind, DailyItem | null>;
  /** Puzzles you have in progress. */
  inProgress: number;
  /** Puzzles in the library. */
  library: number;
}

// ── Stats ───────────────────────────────────────────────────────────────────

export interface StatsResponse {
  user: { id: string; displayName: string };
  /** Whose stats these are, from the viewer's side. */
  whose: 'me' | 'other';
  solved: number;
  solvedThisWeek: number;
  inProgress: number;
  /** Solved puzzles by constraint type (and "classic"), most first. */
  byType: { type: string; count: number }[];
  /** Solved puzzles per score band (0–1, 1–2, ... 9–10) and unrated. */
  scoreSpread: { band: string; count: number }[];
  /** Average solve time per difficulty band (0–2, ... 8–10). */
  timeByBand: { band: string; count: number; averageMs: number | null }[];
  /** For the other person's stats: times count only puzzles you have solved too. */
  timesLimited: boolean;
}
